import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import PageCTA from '../../components/feature/PageCTA';

export default function Tier1QuickPulsePage() {
  const whoThisIsFor = [
    'Idea-stage founders',
    'Early/prototype apps',
    'Founders unsure where to start',
    'Anyone who needs direction before spending more time or money',
    'Founders validating basic concepts'
  ];

  const features = [
    {
      title: 'One-Page Intelligence Scan',
      description: 'A simple summary of how your app aligns with proven success indicators and best-practice benchmarks.',
      icon: 'ri-file-text-line'
    },
    {
      title: 'Risk Profile Snapshot',
      description: 'Shows the top early risks (technical, competitive, or market-related) that could block your progress.',
      icon: 'ri-alert-line'
    },
    {
      title: 'Opportunity Indicator',
      description: 'Clarifies whether your app has high, medium, or low early traction potential.',
      icon: 'ri-compass-line'
    },
    {
      title: '1 Recommended Next Move',
      description: 'One clear, data-backed recommendation based on what will help you MOST — not a long list.',
      icon: 'ri-arrow-right-circle-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 pt-32 pb-20">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/5ceff5a5338535de61efe44a13eca648.jpg')] opacity-5 bg-cover bg-center"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-block px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-6">
            $299
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Tier 1 — Quick Pulse™
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-700 mb-10 max-w-4xl mx-auto leading-relaxed">
            A fast, affordable snapshot of your app's strengths, risks, and early-stage potential — perfect for idea and prototype stages.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/submit-app"
              className="inline-block bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-4 rounded-xl font-semibold text-base hover:from-emerald-700 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get Quick Pulse
            </Link>
            <Link
              to="/sample-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-emerald-700 text-lg font-semibold rounded-lg hover:bg-gray-50 transition-all border-2 border-emerald-200 whitespace-nowrap cursor-pointer"
            >
              Download Sample Report
            </Link>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Who This Is For
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whoThisIsFor.map((item, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl"
              >
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-check-line text-2xl text-emerald-600"></i>
                </div>
                <p className="text-gray-700 font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What You Get
            </h2>
            <p className="text-xl text-gray-600">
              Quick, Clear, Insightful — No Fluff
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl mb-6">
                  <i className={`${feature.icon} text-3xl text-emerald-600`}></i>
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">
            Why It Matters
          </h2>
          
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            Quick Pulse™ helps you avoid blind decisions, misaligned features, wasted time, and wasted development money.
          </p>
          
          <p className="text-xl text-gray-700 leading-relaxed">
            Perfect for founders who need clarity without going deep into the full intelligence suite.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <PageCTA 
        title="Start With a Quick Pulse"
        description="Get a fast, affordable snapshot of your app's potential."
        primaryText="Get Quick Pulse"
        primaryLink="/submit-app"
        bgGradient="from-emerald-600 via-teal-600 to-cyan-600"
      />

      <Footer />
    </div>
  );
}
